/**
 * Voice: one utterance in, almost never anything out.
 *
 * ## Push-to-talk, not always-on — and why it has to be
 *
 * iOS will not reliably run `SpeechRecognition` and our own `getUserMedia` capture at
 * the same time. Safari routes the microphone to the speech service and our AudioWorklet
 * starts receiving silence, or the recogniser refuses to start at all. There is no
 * feature flag for this and no way to detect it before it happens.
 *
 * So the microphone is explicitly handed over and handed back: `pause()` the chord
 * recogniser, take **exactly one** utterance, `resume()`. The resume is in a `finally`
 * — an error in the speech path must never leave the app deaf to the piano, which is
 * the one failure the player would have no way to diagnose.
 *
 * A hold-to-talk button is also the right interaction on its own merits. Always-on
 * dictation beside an acoustic piano transcribes the piano.
 *
 * ## Spoken responses are OFF by default
 *
 * The app must not chatter while someone is playing. Nothing is ever spoken unless the
 * player has turned the toggle on in settings, and even then only short answers to
 * direct questions — never suggestions, never confirmations, never explanations that
 * arrive unasked.
 *
 * ## Degrading
 *
 * Where `SpeechRecognition` is absent (Firefox, most Android browsers, an iPad with
 * dictation disabled) `startPushToTalk` returns `null` and the caller hides the mic
 * button. Silently: text and voice are equivalent here, so a missing microphone costs
 * the player nothing and does not warrant an explanation.
 */

import type { ChordRecognizer } from '@hc/shared';

// ---------------------------------------------------------------------------
// Structural types for the Web Speech API
// ---------------------------------------------------------------------------

/**
 * `SpeechRecognition` is not in TypeScript's DOM lib, and the vendor-prefixed form is
 * the only one that exists on iOS. Declaring the slice we use structurally keeps the
 * module honest about exactly how much of the API it depends on — and makes it
 * mockable in a test with an object literal.
 */
export interface SpeechAlternativeLike {
  transcript: string;
}

export interface SpeechRecognitionEventLike {
  results: ArrayLike<ArrayLike<SpeechAlternativeLike>>;
}

export interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

export type SpeechRecognitionFactory = () => SpeechRecognitionLike;

/** The half of `ChordRecognizer` voice input borrows. */
export type VoicePauseTarget = Pick<ChordRecognizer, 'pause' | 'resume'>;

interface SpeechGlobals {
  SpeechRecognition?: new () => SpeechRecognitionLike;
  webkitSpeechRecognition?: new () => SpeechRecognitionLike;
}

/**
 * Feature-detect the platform speech recogniser.
 *
 * Returns `null` rather than throwing: the absence of dictation is an ordinary
 * configuration, not an error condition.
 */
export function getSpeechRecognitionFactory(scope?: unknown): SpeechRecognitionFactory | null {
  const globals = (scope ?? (typeof globalThis === 'undefined' ? undefined : globalThis)) as
    SpeechGlobals | undefined;
  if (!globals) return null;
  const Ctor = globals.SpeechRecognition ?? globals.webkitSpeechRecognition;
  if (typeof Ctor !== 'function') return null;
  return () => new Ctor();
}

/** True when a mic button is worth rendering at all. */
export function isVoiceInputSupported(scope?: unknown): boolean {
  return getSpeechRecognitionFactory(scope) !== null;
}

// ---------------------------------------------------------------------------
// Push-to-talk
// ---------------------------------------------------------------------------

/** A stuck recogniser must not hold the microphone away from the piano indefinitely. */
export const PUSH_TO_TALK_TIMEOUT_MS = 12_000;

export interface PushToTalkOptions {
  /** Paused for the duration of the utterance, resumed unconditionally afterwards. */
  recognizer?: VoicePauseTarget | null;
  /** Injected in tests; defaults to the platform recogniser. */
  createRecognition?: SpeechRecognitionFactory | null;
  lang?: string;
  timeoutMs?: number;
  /** Called with the transcript before the promise settles. Convenience for the UI. */
  onTranscript?: (transcript: string) => void;
  /** Called with the raw Web Speech error code, for diagnostics only. */
  onError?: (code: string) => void;
}

export interface PushToTalkSession {
  /** Release the button: finish the utterance and resolve with what was heard. */
  stop(): void;
  /** Abandon the utterance entirely. Resolves `null`. */
  cancel(): void;
  /** Resolves once — with the transcript, or `null` if nothing usable was heard. */
  readonly result: Promise<string | null>;
}

/**
 * Take exactly one utterance.
 *
 * Returns `null` when speech input is unavailable, so the caller can decide not to
 * render a mic button at all. When it returns a session, the chord recogniser is
 * already paused and *will* be resumed exactly once, whatever happens next.
 */
export function startPushToTalk(options: PushToTalkOptions = {}): PushToTalkSession | null {
  const factory =
    options.createRecognition === undefined
      ? getSpeechRecognitionFactory()
      : options.createRecognition;
  if (!factory) return null;

  let recognition: SpeechRecognitionLike;
  try {
    recognition = factory();
  } catch {
    return null;
  }

  const recognizer = options.recognizer ?? null;
  let settled = false;
  let transcript: string | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  let resolve!: (value: string | null) => void;
  const result = new Promise<string | null>((res) => {
    resolve = res;
  });

  const finish = (): void => {
    if (settled) return;
    settled = true;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    // The microphone goes back to the piano before anyone is told what was heard.
    try {
      recognizer?.resume();
    } catch {
      /* A recogniser that cannot resume is Chunk C's problem to report, not ours. */
    }
    if (transcript) options.onTranscript?.(transcript);
    resolve(transcript);
  };

  recognition.continuous = false;
  recognition.interimResults = false;
  recognition.maxAlternatives = 1;
  recognition.lang = options.lang ?? defaultLang();

  recognition.onresult = (event) => {
    const best = event.results?.[0]?.[0]?.transcript;
    if (typeof best === 'string' && best.trim()) transcript = best.trim();
  };
  recognition.onerror = (event) => {
    if (event?.error) options.onError?.(event.error);
    finish();
  };
  recognition.onend = finish;

  try {
    recognizer?.pause();
  } catch {
    /* Pausing is best-effort; a recogniser that is not running throws nothing useful. */
  }

  try {
    recognition.start();
  } catch {
    finish();
    return { stop: () => {}, cancel: () => {}, result };
  }

  timer = setTimeout(() => {
    try {
      recognition.abort();
    } catch {
      /* ignore */
    }
    finish();
  }, options.timeoutMs ?? PUSH_TO_TALK_TIMEOUT_MS);

  return {
    stop() {
      if (settled) return;
      try {
        recognition.stop();
      } catch {
        finish();
      }
    },
    cancel() {
      if (settled) return;
      transcript = null;
      try {
        recognition.abort();
      } catch {
        /* ignore */
      }
      finish();
    },
    result,
  };
}

/**
 * Start and await one utterance, letting the platform decide when the player has
 * stopped speaking. The tap-to-talk counterpart of `startPushToTalk`.
 */
export async function captureUtterance(options: PushToTalkOptions = {}): Promise<string | null> {
  const session = startPushToTalk(options);
  if (!session) return null;
  return session.result;
}

function defaultLang(): string {
  if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
    return navigator.language;
  }
  return 'en-GB';
}

// ---------------------------------------------------------------------------
// Spoken responses — opt-in, short, direct answers only
// ---------------------------------------------------------------------------

export const SPOKEN_RESPONSES_KEY = 'hc.spokenResponses';

/** Anything longer than this is not an answer, it is a lecture. Truncated at a word. */
export const MAX_SPOKEN_CHARS = 160;

export interface SpeechSynthesisLike {
  speak(utterance: object): void;
  cancel(): void;
}

export interface SpeakOptions {
  synth?: SpeechSynthesisLike | null;
  createUtterance?: ((text: string) => object) | null;
  lang?: string;
}

interface SynthGlobals {
  speechSynthesis?: SpeechSynthesisLike;
  SpeechSynthesisUtterance?: new (text: string) => object & { lang?: string };
}

function synthGlobals(): SynthGlobals {
  return (typeof globalThis === 'undefined' ? {} : globalThis) as SynthGlobals;
}

/** True when the settings toggle is worth showing. */
export function isSpeechOutputSupported(): boolean {
  const globals = synthGlobals();
  return (
    typeof globals.speechSynthesis === 'object' &&
    globals.speechSynthesis !== null &&
    typeof globals.SpeechSynthesisUtterance === 'function'
  );
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Safari in private mode throws on access rather than returning null.
    return null;
  }
}

/** **Defaults to false.** The app is silent until the player asks for it not to be. */
export function loadSpokenResponsesEnabled(
  storage: StorageLike | null = defaultStorage(),
): boolean {
  try {
    return storage?.getItem(SPOKEN_RESPONSES_KEY) === 'true';
  } catch {
    return false;
  }
}

export function saveSpokenResponsesEnabled(
  value: boolean,
  storage: StorageLike | null = defaultStorage(),
): void {
  try {
    storage?.setItem(SPOKEN_RESPONSES_KEY, value ? 'true' : 'false');
  } catch {
    /* Nothing here is worth failing a settings toggle over. */
  }
}

/** Trim an answer down to something that can be said over a piano without irritating. */
export function clampSpokenText(text: string, limit = MAX_SPOKEN_CHARS): string {
  const clean = text.trim().replace(/\s+/g, ' ');
  if (clean.length <= limit) return clean;
  const cut = clean.slice(0, limit);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

/**
 * Say one short thing, replacing anything already queued.
 *
 * Cancelling first is deliberate: two answers overlapping is worse than losing the
 * older one, and a queue that drains while the player is mid-phrase is exactly the
 * chatter this feature is fenced off to avoid. Returns false when nothing was said.
 */
export function speak(text: string, options: SpeakOptions = {}): boolean {
  const clean = clampSpokenText(text);
  if (!clean) return false;

  const globals = synthGlobals();
  const synth = options.synth === undefined ? (globals.speechSynthesis ?? null) : options.synth;
  const create =
    options.createUtterance === undefined
      ? globals.SpeechSynthesisUtterance
        ? (value: string) => {
            const Utterance = globals.SpeechSynthesisUtterance as new (
              text: string,
            ) => object & { lang?: string };
            const utterance = new Utterance(value);
            if (options.lang) utterance.lang = options.lang;
            return utterance;
          }
        : null
      : options.createUtterance;

  if (!synth || !create) return false;

  try {
    synth.cancel();
    synth.speak(create(clean));
    return true;
  } catch {
    return false;
  }
}

/**
 * The `speak` callback for `CommandEnv`, or `undefined` when the player has not opted
 * in. `undefined` rather than a no-op so that `env.speak?.(…)` reads as "speaking is
 * not configured" at every call site.
 */
export function createSpeaker(
  enabled: boolean,
  options: SpeakOptions = {},
): ((text: string) => void) | undefined {
  if (!enabled) return undefined;
  return (text: string) => {
    speak(text, options);
  };
}
